import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Modal } from '@/common';
import { UploadButton } from '@/components/common';
import { Button, Label, LABEL_TYPE, Spinner, ZeroState } from '@/components/generic';
import { ButtonVariant } from '@/components/generic/Button/button-config';
import { COVER_PHOTO, IMAGE, UPLOAD_PICKER_TYPES } from '@/constants';
import { AuthContext, EventContext } from '@/contexts';
import { AssetScope, AssetType, PERMISSION_TYPE } from '@/enums';
import { ORGANIZATION_PERMISSION_TYPE } from '@/enums/OrganizationPermissionEnum';
import { groupBy } from '@/helpers';
import { useFileUpload } from '@/hooks/useFileUpload';
import { AssetsContainer } from '@/pages/Event/Asset/AssetsContainer';
import {
  GrayImageIcon,
  ImageBlueBoldIcon as ImageIcon,
  UploadPhotosPlaceholder
} from '@/resources';
import { useGetEventAssets, useGetEventTags } from '@/services';
import { TestIds } from '@/shared/test-ids';
import { Asset, AssetSlot, Tag } from '@/types';

type CoverPhotosProps = {
  isMobileView?: boolean;
  organizationPermissions?: string[];
};

export const CoverPhotos = ({
  isMobileView = false,
  organizationPermissions: eventOrganizationPermissions
}: CoverPhotosProps) => {
  const [t] = useTranslation('common');
  const navigate = useNavigate();
  const { organizationPermissions: userOrganizationPermissions, hasPermission } =
    useContext(AuthContext);
  const organizationPermissions = eventOrganizationPermissions ?? userOrganizationPermissions;
  const { eventData, isEventLoading } = useContext(EventContext);
  const hasOrgAccess = hasPermission(PERMISSION_TYPE.ORG_ACCESS);
  const hasCoverPhotoCreationAccess = organizationPermissions.includes(
    ORGANIZATION_PERMISSION_TYPE.COVER_PHOTO_CREATION
  );
  const hasContentManagementAccess = organizationPermissions.includes(
    ORGANIZATION_PERMISSION_TYPE.CONTENT_MANAGEMENT
  );
  const { isLoading: isLoadingTags, data: allTags } = useGetEventTags(eventData?.id ?? '');

  const {
    data: assetsAll,
    isLoading: isLoadingAssets,
    refetch: refetchEventAssets
  } = useGetEventAssets(
    eventData?.id ?? '',
    hasOrgAccess
      ? []
      : (allTags?.data
          ?.filter((tag: Tag) => tag.name === COVER_PHOTO)
          .map((tag: Tag) => `${tag.id}`) ?? []),
    0,
    '',
    [
      AssetType.COVER_PHOTO.toString(),
      ...(hasOrgAccess
        ? []
        : eventData?.relatedUsersCanGenerate
          ? []
          : [AssetType.IMAGE.toString()])
    ],
    undefined,
    true,
    !isLoadingTags && hasContentManagementAccess
  );

  const { uploadFiles: uploadCoverPhotoFiles, isUploading: isUploadingCoverPhoto } = useFileUpload({
    eventId: eventData?.id,
    organizationId: eventData?.organization?.id,
    pickerTypes: UPLOAD_PICKER_TYPES.IMAGE,
    isPublic: false,
    assetScope: AssetScope.PRIVATE,
    assetTag: COVER_PHOTO,
    onRefetchNeeded: refetchEventAssets
  });

  const allAssets = useMemo(() => {
    return assetsAll?.pages.flatMap((page) => page.data?.items) || [];
  }, [assetsAll]);

  const handleCreateCoverPhotos = () => {
    isMobileView
      ? navigate(`/events/${eventData?.id}/upload-content/cover-photos/create`)
      : navigate(`/events/${eventData?.id}/cover-photos/create`);
  };

  const handleEditCoverPhoto = (selectedAsset: Asset) => {
    isMobileView
      ? navigate(`/events/${eventData?.id}/upload-content/cover-photos/${selectedAsset?.id}/edit`)
      : navigate(`/events/${eventData?.id}/cover-photos/${selectedAsset?.id}/edit`);
  };

  const groupedAssetSlots = groupBy(
    eventData?.assetSlots as unknown as AssetSlot[],
    ({ slotType }: AssetSlot) => slotType?.assetType
  );
  const assets = allAssets.filter((asset: Asset) => asset.tags.length > 0);
  const groupedAssets = groupBy(assets ?? [], ({ assetType }: Asset) => assetType);

  const groupedImageAndCoverPhotoAssetsNumber =
    (groupedAssets.get(COVER_PHOTO)?.length ?? 0) +
    (eventData?.relatedUsersCanGenerate ? 0 : (groupedAssets.get(IMAGE)?.length ?? 0));

  const handleDisable = () => {
    return !!(
      groupedImageAndCoverPhotoAssetsNumber >= Number(groupedAssetSlots.get(COVER_PHOTO)?.length)
    );
  };

  return (
    <>
      {isLoadingTags || isEventLoading || (hasContentManagementAccess && isLoadingAssets) ? (
        <Spinner />
      ) : (
        <div
          data-testid={TestIds.coverPhotos.page}
          className={`flex size-full flex-col ${hasOrgAccess ? 'w-full' : 'max-w-[960px]'} ${hasCoverPhotoCreationAccess && hasContentManagementAccess ? '' : 'relative'}`}>
          <div className={`mt-4 flex flex-col md:mt-9 ${hasOrgAccess && 'md:mr-20'}`}>
            {!hasOrgAccess && (
              <>
                <div
                  className={`flex w-full flex-row items-center justify-between pb-4 ${
                    !hasOrgAccess && 'mb-4 border-b border-secondary-blue-gray'
                  }`}>
                  <Label text={t('coverPhotos')} type={LABEL_TYPE.H2} className='text-xl' />
                  <div className='z-10 inline-flex flex-col items-end gap-2.5 px-2'>
                    <div
                      data-testid={TestIds.coverPhotos.counter}
                      className='font-sans text-xl font-semibold text-primary-blue'>
                      {`${groupedImageAndCoverPhotoAssetsNumber}/${
                        groupedAssetSlots.get(COVER_PHOTO)?.length
                      }`}
                    </div>
                  </div>
                </div>
                {eventData?.relatedUsersCanGenerate ? (
                  <Label
                    type={LABEL_TYPE.PARAGRAPH2}
                    text={t('pleaseCreateCoverPhoto')}
                    className='pb-4 font-sans text-accent-dark-gray'
                  />
                ) : (
                  <Label
                    type={LABEL_TYPE.PARAGRAPH2}
                    text={t('pleaseUploadPhotoToUseAsPortrait')}
                    className='pb-4 font-sans text-accent-dark-gray'
                  />
                )}
              </>
            )}
            {hasOrgAccess && (
              <div className='flex md:justify-end'>
                <Button
                  data-testid={TestIds.coverPhotos.createButton}
                  variant={ButtonVariant.SECONDARY}
                  handleOnClick={handleCreateCoverPhotos}
                  disabled={isMobileView && handleDisable()}
                  className={`w-full md:w-fit ${isMobileView && handleDisable() && 'text-secondary-steel-blue'}`}>
                  <ImageIcon
                    className={`mr-2.5 md:size-4 ${isMobileView && handleDisable() && 'opacity-60'}`}
                  />
                  {t('createCoverPhoto')}
                </Button>
              </div>
            )}
          </div>
          {!isLoadingTags && allAssets.length !== 0 ? (
            <div
              className={`h-full max-h-[80%] md:max-h-full ${
                !hasOrgAccess && 'max-w-[960px] rounded-md border border-secondary-blue-gray p-2.5'
              }`}>
              <AssetsContainer
                eventId={String(eventData?.id)}
                justContent={true}
                assetType={AssetType.COVER_PHOTO.toString()}
                className={`!px-0 ${hasOrgAccess ? 'mt-6 md:mt-9 md:!px-20' : '!mt-0 md:px-0'}`}
                assetTag={
                  hasOrgAccess
                    ? []
                    : (allTags?.data?.filter((tag: Tag) => tag.name === COVER_PHOTO) ?? [])
                }
                canBeEdited={true}
                editSelectedAsset={handleEditCoverPhoto}
                assetContainerStyles='md:h-[52vh] 2xl:h-[58vh] 3xl:h-[63vh] 4xl:h-[63vh]'
                relatedUsersCanGenerate={eventData?.relatedUsersCanGenerate}
              />
            </div>
          ) : hasOrgAccess ? (
            <div
              data-testid={TestIds.coverPhotos.zeroState}
              className='mt-16 flex items-center justify-center md:mt-0 md:h-full'>
              <ZeroState
                icon={<GrayImageIcon />}
                titleFirstLine={t('createCoverPhotoTitleOne')}
                titleSecondLine={t('createCoverPhotoTitleTwo')}
                onIconClick={handleCreateCoverPhotos}
              />
            </div>
          ) : (
            <div className='flex min-h-[140px] flex-col items-center justify-center rounded-[10px] border border-secondary-pale-blue-gray md:min-w-[960px]'>
              <UploadPhotosPlaceholder className='text-[22px]' />
              <Label
                type={LABEL_TYPE.PARAGRAPH2}
                text={t('uploadPhotos')}
                className='pt-[7px] font-sans !text-xs font-medium text-secondary-gray'
              />
            </div>
          )}
          {!hasOrgAccess && (
            <>
              {eventData?.relatedUsersCanGenerate ? (
                <div className='mt-4 flex md:justify-end'>
                  <Button
                    data-testid={TestIds.coverPhotos.createButton}
                    variant={ButtonVariant.SECONDARY}
                    className={`w-1/2 md:w-fit ${handleDisable() && 'sm:text-secondary-steel-blue'}`}
                    handleOnClick={handleCreateCoverPhotos}
                    disabled={handleDisable()}>
                    {t('createCoverPhoto')}
                  </Button>
                </div>
              ) : (
                <UploadButton
                  multiple={false}
                  text='uploadNewPhoto'
                  pickerTypes={UPLOAD_PICKER_TYPES.IMAGE}
                  isUploading={isUploadingCoverPhoto}
                  disabled={handleDisable()}
                  showMaxFileSize
                  icon={null}
                  className='mt-4 h-9 cursor-pointer'
                  onFilesSelected={uploadCoverPhotoFiles}
                />
              )}
            </>
          )}
          {(!hasCoverPhotoCreationAccess || !hasContentManagementAccess) && (
            <div className='absolute inset-0 z-10 backdrop-blur-sm'>
              <Modal
                isOpen={!hasCoverPhotoCreationAccess || !hasContentManagementAccess}
                modalClasses='md:w-[450px] w-full border border-secondary-blue-gray'
                hasCloseIcon={false}
                onClose={() => {
                  undefined;
                }}>
                <div className='flex w-full flex-col items-center justify-center gap-y-4 text-center'>
                  <Label type={LABEL_TYPE.PARAGRAPH1_BOLD} text={t('featureUnavailable')} />
                  <Label
                    type={LABEL_TYPE.PARAGRAPH2}
                    text={t('yourOranizationDoesntHavePermission', {
                      permission: hasCoverPhotoCreationAccess
                        ? hasContentManagementAccess
                          ? ''
                          : t(`${ORGANIZATION_PERMISSION_TYPE.CONTENT_MANAGEMENT}`)
                        : t(`${ORGANIZATION_PERMISSION_TYPE.COVER_PHOTO_CREATION}`)
                    })}
                  />
                </div>
              </Modal>
            </div>
          )}
        </div>
      )}
    </>
  );
};
